import { useState, useCallback } from 'react';
import {
  Card,
  TextField,
  ResourceList,
  ResourceItem,
  Thumbnail,
  Text,
  Button,
  Select,
  InlineStack,
  BlockStack,
  Badge,
  Icon,
  Box,
  Banner,
  Spinner
} from '@shopify/polaris';
import { SearchIcon, PlusIcon } from '@shopify/polaris-icons';

/**
 * ProductSelector — lists product VARIANT rows (each row = one printable unit).
 * Supports:
 *  - search by title / SKU / barcode / variant
 *  - quantity per row + Add to queue
 *  - batch selection ("Add all to queue")
 *  - generate + write back barcodes for variants missing one
 */
function ProductSelector({
  products,
  selectedProducts,
  onSelect,
  onAddToQueue,
  presets,
  selectedPreset,
  onPresetChange,
  onBarcodeGenerated,
  loading
}) {
  const [searchValue, setSearchValue] = useState('');
  const [quantities, setQuantities] = useState({});
  const [generatingIds, setGeneratingIds] = useState({});
  const [bulkGenerating, setBulkGenerating] = useState(false);

  const filteredProducts = products.filter((row) => {
    const search = searchValue.toLowerCase();
    return (
      row.title.toLowerCase().includes(search) ||
      (row.sku || '').toLowerCase().includes(search) ||
      (row.barcode || '').toLowerCase().includes(search) ||
      (row.variantTitle || '').toLowerCase().includes(search)
    );
  });

  const missingBarcodeCount = products.filter((r) => !r.hasBarcode).length;

  const handleQuantityChange = useCallback((rowId, value) => {
    setQuantities((prev) => ({
      ...prev,
      [rowId]: Math.max(1, parseInt(value) || 1)
    }));
  }, []);

  // Generate a barcode for one variant (writes back to Shopify)
  const handleGenerateBarcode = useCallback(
    async (row) => {
      setGeneratingIds((prev) => ({ ...prev, [row.id]: true }));
      try {
        const response = await fetch(
          `/api/products/${encodeURIComponent(row.productId)}/barcode`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ variantId: row.id, sku: row.sku })
          }
        );
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Failed to generate barcode');
        }
        if (onBarcodeGenerated) {
          onBarcodeGenerated(row.id, data.barcode);
        }
      } catch (error) {
        console.error('Barcode generation error:', error);
        window.alert(`Could not generate barcode: ${error.message}`);
      } finally {
        setGeneratingIds((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
      }
    },
    [onBarcodeGenerated]
  );

  // Generate barcodes for ALL visible variants missing one
  const handleBulkGenerate = useCallback(async () => {
    const targets = filteredProducts.filter((r) => !r.hasBarcode);
    if (targets.length === 0) return;

    setBulkGenerating(true);
    try {
      const response = await fetch('/api/products/bulk-barcode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variants: targets.map((t) => ({
            productId: t.productId,
            variantId: t.id,
            sku: t.sku
          }))
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Bulk generation failed');
      }
      if (onBarcodeGenerated) {
        (data.generated || []).forEach((g) => onBarcodeGenerated(g.variantId, g.barcode));
      }
      if (data.failed && data.failed.length > 0) {
        window.alert(`${data.failed.length} barcode(s) could not be generated. Check permissions and try again.`);
      }
    } catch (error) {
      console.error('Bulk barcode generation error:', error);
      window.alert(`Bulk generation failed: ${error.message}`);
    } finally {
      setBulkGenerating(false);
    }
  }, [filteredProducts, onBarcodeGenerated]);

  const presetOptions = presets.map((preset) => ({
    label: preset.name,
    value: preset.id
  }));

  if (loading) {
    return (
      <Box padding="800">
        <BlockStack gap="400" inlineAlign="center">
          <Spinner size="large" />
          <Text tone="subdued">Loading products from Shopify…</Text>
        </BlockStack>
      </Box>
    );
  }

  return (
    <Box padding="400">
      <BlockStack gap="400">
        {/* Search and Preset Selection */}
        <InlineStack gap="400" align="space-between">
          <div style={{ flex: 1 }}>
            <TextField
              label="Search products"
              labelHidden
              placeholder="Search by title, SKU, variant, or barcode..."
              value={searchValue}
              onChange={setSearchValue}
              prefix={<Icon source={SearchIcon} />}
              clearButton
              onClearButtonClick={() => setSearchValue('')}
              autoComplete="off"
            />
          </div>
          <div style={{ width: '250px' }}>
            <Select
              label="Printer preset"
              labelHidden
              options={presetOptions}
              value={selectedPreset}
              onChange={onPresetChange}
            />
          </div>
        </InlineStack>

        {/* Missing barcodes helper */}
        {missingBarcodeCount > 0 && (
          <Banner
            tone="info"
            title={`${missingBarcodeCount} variant(s) are missing a barcode`}
            action={{
              content: bulkGenerating ? 'Generating…' : 'Generate all',
              onAction: handleBulkGenerate,
              loading: bulkGenerating
            }}
          >
            <p>
              ProLabel Wizard can create unique barcodes and save them directly to
              your products. Labels will print with a “No barcode” placeholder until
              a barcode exists.
            </p>
          </Banner>
        )}

        {/* Product List */}
        <Card>
          <ResourceList
            resourceName={{ singular: 'variant', plural: 'variants' }}
            items={filteredProducts}
            renderItem={(row) => {
              const isSelected = selectedProducts.some((p) => p.id === row.id);
              const quantity = quantities[row.id] || 1;
              const isGenerating = !!generatingIds[row.id];

              return (
                <ResourceItem
                  id={row.id}
                  onClick={() => onSelect(row)}
                  selected={isSelected}
                  accessibilityLabel={`Select ${row.title}`}
                >
                  <InlineStack gap="400" align="space-between" blockAlign="center">
                    <InlineStack gap="400" blockAlign="center">
                      <Thumbnail
                        source={row.image || '/img/product-placeholder.svg?v=2'}
                        alt={row.title}
                        size="small"
                      />
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          <Text variant="bodyMd" fontWeight="semibold">
                            {row.title}
                          </Text>
                          {row.variantTitle && (
                            <Badge tone="info">{row.variantTitle}</Badge>
                          )}
                        </InlineStack>
                        <InlineStack gap="200">
                          {row.sku && (
                            <Text variant="bodySm" tone="subdued">
                              SKU: {row.sku}
                            </Text>
                          )}
                          {row.hasBarcode ? (
                            <Badge>{row.barcode}</Badge>
                          ) : (
                            <Badge tone="attention">No barcode</Badge>
                          )}
                        </InlineStack>
                        <InlineStack gap="200">
                          <Text variant="bodyMd" fontWeight="bold">
                            {row.price ? `$${parseFloat(row.price).toFixed(2)}` : '—'}
                          </Text>
                          {row.comparePrice && (
                            <Text
                              variant="bodySm"
                              tone="subdued"
                              textDecorationLine="line-through"
                            >
                              ${parseFloat(row.comparePrice).toFixed(2)}
                            </Text>
                          )}
                        </InlineStack>
                      </BlockStack>
                    </InlineStack>

                    <InlineStack gap="200" blockAlign="center">
                      {!row.hasBarcode && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateBarcode(row);
                          }}
                          loading={isGenerating}
                          size="slim"
                        >
                          Generate barcode
                        </Button>
                      )}
                      <div style={{ width: '80px' }}>
                        <TextField
                          label="Quantity"
                          labelHidden
                          type="number"
                          min={1}
                          value={quantity.toString()}
                          onChange={(value) => handleQuantityChange(row.id, value)}
                          autoComplete="off"
                        />
                      </div>
                      <Button
                        icon={PlusIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddToQueue(row, quantity);
                        }}
                      >
                        Add
                      </Button>
                    </InlineStack>
                  </InlineStack>
                </ResourceItem>
              );
            }}
            emptyState={
              <Box padding="800">
                <BlockStack gap="200" inlineAlign="center">
                  <Text variant="bodyMd" tone="subdued">
                    {searchValue
                      ? 'No variants match your search'
                      : 'No products available yet'}
                  </Text>
                </BlockStack>
              </Box>
            }
          />
        </Card>

        {/* Selection Summary */}
        {selectedProducts.length > 0 && (
          <Card>
            <Box padding="400">
              <InlineStack gap="400" align="space-between" blockAlign="center">
                <Text variant="bodyMd">
                  {selectedProducts.length} variant(s) selected
                </Text>
                <Button
                  variant="primary"
                  icon={PlusIcon}
                  onClick={() => {
                    selectedProducts.forEach((row) => {
                      onAddToQueue(row, quantities[row.id] || 1);
                    });
                  }}
                >
                  Add all to Print Queue
                </Button>
              </InlineStack>
            </Box>
          </Card>
        )}
      </BlockStack>
    </Box>
  );
}

export default ProductSelector;
